import { useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountRow } from "./AccountRow";

export function ChangePasswordDialog({ user, onDone }) {
  const hasPw = !!user?.has_password;
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next.length < 6) return toast.error("New password must be at least 6 characters.");
    if (next !== confirm) return toast.error("Passwords do not match.");
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password: hasPw ? current : undefined, new_password: next });
      toast.success(hasPw ? "Password changed" : "Password set");
      setCurrent(""); setNext(""); setConfirm("");
      await onDone?.();
      setOpen(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="w-full" data-testid="open-password-dialog">
          <AccountRow icon={Lock} title={hasPw ? "Change Password" : "Set Password"} value={hasPw ? "••••••••" : "No password set — add one"} testid="account-row-password" />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>{hasPw ? "Change Password" : "Set a Password"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {hasPw && (
            <div className="space-y-1.5">
              <Label>Current password</Label>
              <Input type="password" data-testid="current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="password" data-testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <Input type="password" data-testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="password-save-btn" onClick={save} disabled={saving}
            className="h-10 px-5 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
            {saving ? "Saving…" : (hasPw ? "Update password" : "Set password")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
